import { useDispatch } from "react-redux";
import { formatCalendarDateToString } from "../../utils/dates";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { manageFirmSlice } from "../../redux";
import type {
  MultiplePrimaryJurisdictionMemberRow,
  SecondaryJurisdictionMemberRow,
} from "../../interfaces/models/IManageFirmOverview";
import TableHorizontalScrollContainer from "../../components/Organismos/Table/TableHorizontalScrollContainer";
import SortDefault from "../../components/Atomos/Icons/SortDefault";
import ManageFirmOfficeRowActions, {
  type ManageFirmOfficeRowActionHandlers,
} from "./ManageFirmOfficeRowActions";
import {
  useProfileOfficeTabSelection,
  type ProfileOfficeTab,
} from "./Professionals/ProfileOfficeTabs";
import { MANAGE_FIRM_STATS_SECTION_TITLE_CLASS } from "./manageFirmStatsPanelStyles";
import "../../assets/css/table/Table.css";

const MULTIPLE_PRIMARY_TAB_ID = "multiple-primary";
const SECONDARY_TAB_ID = "secondary";

const TABS: ProfileOfficeTab[] = [
  { id: MULTIPLE_PRIMARY_TAB_ID, label: "Multiple Primary" },
  { id: SECONDARY_TAB_ID, label: "Secondary" },
];

type JurisdictionMemberRow =
  | MultiplePrimaryJurisdictionMemberRow
  | SecondaryJurisdictionMemberRow;

type ManageFirmJurisdictionMembersPanelProps = {
  multiplePrimaryRows: MultiplePrimaryJurisdictionMemberRow[] | null | undefined;
  secondaryRows: SecondaryJurisdictionMemberRow[] | null | undefined;
  loading?: boolean;
  actionHandlers: ManageFirmOfficeRowActionHandlers;
};

type SortableHeaderProps = {
  label: string;
  sortable?: boolean;
  className?: string;
};

function SortableHeader({
  label,
  sortable = true,
  className = "",
}: SortableHeaderProps): JSX.Element {
  return (
    <th
      className={`px-3 py-3 text-left text-sm font-semibold text-neutral-dark ${className}`}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortable ? (
          <span className="table-sort-icon text-neutral-dark" aria-hidden>
            <SortDefault />
          </span>
        ) : null}
      </div>
    </th>
  );
}

function JurisdictionMembersTabs({
  tabs,
  activeTabId,
  onSelect,
}: {
  tabs: ProfileOfficeTab[];
  activeTabId: string;
  onSelect: (tabId: string) => void;
}): JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap gap-2" role="tablist">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab.id)}
            className={`rounded-md px-5 py-2 text-sm font-medium transition-colors ${
              isActive
                ? "bg-accent-Default-dark text-white"
                : "bg-accent-Default-light text-Default hover:bg-[#d9ebff]"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function formatJoinedDate(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return formatCalendarDateToString(parsed);
}

export default function ManageFirmJurisdictionMembersPanel({
  multiplePrimaryRows,
  secondaryRows,
  loading = false,
  actionHandlers,
}: ManageFirmJurisdictionMembersPanelProps): JSX.Element {
  const dispatch = useDispatch();
  const { activeTabId, setActiveTabId } = useProfileOfficeTabSelection(TABS);

  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-64 rounded bg-gray-200" />
          <div className="h-40 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  const isMultiplePrimary = activeTabId === MULTIPLE_PRIMARY_TAB_ID;
  const rows: JurisdictionMemberRow[] | null | undefined = isMultiplePrimary
    ? multiplePrimaryRows
    : secondaryRows;

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className={`${MANAGE_FIRM_STATS_SECTION_TITLE_CLASS} mb-4`}>
        Multiple Primary / Secondary Jurisdiction Members
      </h3>

      <JurisdictionMembersTabs
        tabs={TABS}
        activeTabId={activeTabId}
        onSelect={setActiveTabId}
      />

      {!rows ? (
        <p className="text-sm text-gray-font">
          {isMultiplePrimary
            ? "Multiple Primary Jurisdiction Members is unavailable."
            : "Secondary Jurisdiction Members is unavailable."}
        </p>
      ) : (
        <TableHorizontalScrollContainer
          measureKey={`${activeTabId}-${rows.length}`}
          maxBodyHeight="400px"
        >
          <table className="min-w-full w-max border-collapse text-sm text-gray-font">
            <thead className="sticky top-0 z-10 border-b border-gray-200 bg-white">
              <tr>
                <SortableHeader label="#" sortable={false} className="w-10" />
                <SortableHeader label="Jurisdiction" />
                <SortableHeader label="Region" />
                <SortableHeader label="Firm Name" />
                <SortableHeader label="ID" />
                <SortableHeader label="Joined" />
                <SortableHeader label="Action" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const stripeClass =
                  index % 2 === 0 ? "bg-white" : "bg-gray-25";
                const isAcceptedListing =
                  isMultiplePrimary && row.legacyTypeListing === "A";
                const rowClass = isAcceptedListing
                  ? index % 2 === 0
                    ? "bg-[#ECD9D9]"
                    : "bg-[#DBB7B7]"
                  : stripeClass;

                return (
                  <tr
                    key={row.officeId}
                    className={`border-b border-gray-100 last:border-0 ${rowClass}`}
                  >
                    <td className="px-3 py-2.5 text-gray-font">{index + 1}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-font">
                      {row.jurisdiction}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-font">
                      {row.region}
                    </td>
                    <td className="px-3 py-2.5">
                      <PersistentLink
                        className="font-medium text-Default hover:underline"
                        to={`Dashboard/${row.firmId}`}
                        onClick={() => {
                          dispatch(
                            manageFirmSlice.actions.SetFirm({
                              id: row.firmId,
                              firmName: row.firmName,
                              name: row.firmName,
                            }),
                          );
                        }}
                      >
                        {row.firmName}
                      </PersistentLink>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-gray-font">
                      {row.companyId}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-font">
                      {formatJoinedDate(row.dateJoined)}
                    </td>
                    <td className="px-3 py-2.5">
                      <ManageFirmOfficeRowActions
                        officeId={row.officeId}
                        firmId={row.firmId}
                        firmName={
                          row.jurisdiction
                            ? `${row.firmName} — ${row.jurisdiction}`
                            : row.firmName
                        }
                        jurisdiction={row.jurisdiction}
                        showFirmActions
                        showOfficeLog
                        officeScope={{
                          kind: "office",
                          officeId: row.officeId,
                        }}
                        handlers={actionHandlers}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableHorizontalScrollContainer>
      )}
    </div>
  );
}
